import { HistoricalStockPricesErrorType, getHistoricalStockPrices } from '@egw/api'
import { Logger } from '@egw/logger'
import { TimeseriesChart } from '../components/timeseries-chart/timeseries-chart'
import { filterDataByPeriod, TimePeriod, transformStockData } from '../components/utils'

const timePeriods = {
  [TimePeriod.OneMonth]: '1 Month',
  [TimePeriod.ThreeMonths]: '3 Months',
  [TimePeriod.SixMonths]: '6 Months',
  [TimePeriod.OneYear]: '1 Year',
  [TimePeriod.FiveYears]: '5 Years',
}

const getStartDate = (timespan: string): Date => {
  const [countStr, unit] = timespan.split('_')
  const count = Number(countStr)
  const end = new Date()
  const start = new Date(end)

  if (unit === 'm') {
    start.setMonth(start.getMonth() - count)
  } else if (unit === 'y') {
    start.setFullYear(start.getFullYear() - count)
  } else {
    start.setMonth(start.getMonth() - 1)
  }

  return start
}

const log = new Logger('TimeseriesChart Action')

export const showStockTimeseriesAction = {
  available: 'remote',
  name: 'show_stock_timeseries',
  description:
    'Display an individual security price using a timeseries visualization with up to five years of historical data using daily close of market pricing.',
  parameters: [
    {
      name: 'symbol',
      type: 'string',
      description:
        'U.S. securities and ADRs listed on NYSE or NASDAQ exchanges, ETFs, and mutual funds. When looking up indices, such as the S&P 500, an ETF equivalent must be used.',
      required: true,
    },
    {
      name: 'timespan',
      type: 'string',
      description: '5_y,1_y,6_m,3_m,1_m. Defaults to 1_y',
      required: true,
    },
  ],

  handler: async ({ symbol, timespan }: { symbol: string; timespan: string }) => {
    try {
      const startDate = new Date(new Date().getFullYear(), new Date().getMonth() - 60, new Date().getDate())
        .toISOString()
        .split('T')[0]
      const endDate = new Date().toISOString().split('T')[0]

      const data = await getHistoricalStockPrices({
        ticker: symbol.toUpperCase(),
        startDate,
        endDate,
      })

      if (
        data.__typename === 'HistoricalStockPricesError' &&
        data.errorType === HistoricalStockPricesErrorType.TICKER_NOT_FOUND
      ) {
        return {
          success: false,
          error: `${symbol} is not a valid ticker symbol. You must provide a valid US stock symbol`,
        }
      }

      const chartData = transformStockData(data.__typename === 'HistoricalStockPrices' ? data.prices : [])
      const filteredData = chartData.length > 0 ? filterDataByPeriod(chartData, timespan as TimePeriod) : []

      const displayData = filteredData.length > 0 ? filteredData : chartData.slice(-1)
      const hasData = displayData.length > 0

      const todayPrice = chartData[0]?.value ?? 0
      const periodStartPrice = hasData ? (displayData[displayData.length - 1]?.value ?? todayPrice) : 0

      const totalChange = todayPrice - periodStartPrice
      const totalReturn = periodStartPrice > 0 ? (totalChange / periodStartPrice) * 100 : 0
      const isPositive = totalReturn >= 0

      const start = getStartDate(timespan)
      const startDateAltText = start.toISOString().split('T')[0]

      const altText = `A timeseries graph showing ${symbol} stock over the period of "${timePeriods[timespan as TimePeriod]}" (${startDateAltText} through ${filteredData[0].date}). The current price is $${todayPrice} and the performance during the period is ${isPositive ? '+' : '-'}${Math.abs(totalReturn).toFixed(2)}%`

      return { success: true, alt_text: altText }
    } catch (error) {
      log.error('Error checking symbol:', error)
      return {
        success: false,
        error: `Failed to validate symbol ${symbol}. Please try again.`,
      }
    }
  },

  render: ({ args, status }: { status: string; args: { symbol: string; timespan: string } }) => {
    return status === 'complete' ? (
      <TimeseriesChart key={`stock-${args.symbol}`} symbol={args.symbol} timespan={args.timespan} />
    ) : null
  },
}
